/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { isAfter, isSameDay, parseISO } from 'date-fns';
import isBefore from 'date-fns/isBefore';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';
import { getImageAtSize } from '../../../lib/utils';

import { useTheme, useThemeClassName } from '../../../contexts/ThemeContext';
import Button from '../../buttons/Button';
import CircleCheckIcon from '../../icons/CircleCheck';
import LockIcon from '../../icons/Lock';
import ExplanationModal from '../../modals/ExplanationModal';

import styles from '../../../../styles/items/games/iga-noel.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    canParticipate: PropTypes.bool,
    className: PropTypes.string,
    cardClassName: PropTypes.string,
};

const defaultProps = {
    game: null,
    canParticipate: true,
    className: null,
    cardClassName: null,
};

function IgaNoelItem({ className, canParticipate, cardClassName, game }) {
    const themeClassName = useThemeClassName(styles);

    const {
        type,
        url,
        title,
        image = null,
        startAt = null,
        endAt = null,
        hasParticipation = false,
        explanationModal,
    } = game || {};

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => setModalOpen(true), [setModalOpen]);
    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);
    const endAtDate = useMemo(() => (endAt !== null ? parseISO(endAt) : null), [endAt]);

    const isStarted = useMemo(
        () => startAtDate === null || isAfter(new Date(), startAtDate),
        [startAtDate],
    );
    const isEnded = useMemo(
        () => endAtDate !== null && isAfter(new Date(), endAtDate),
        [endAtDate],
    );

    const canPlay = useMemo(() => {
        const date = new Date();

        return (
            startAtDate === null || isAfter(date, startAtDate)
            // && canParticipate // @TODO condition in /pages/Play not calibrated for this contest
        );
    }, [startAtDate, canParticipate]);

    const isToday = useMemo(() => {
        const date = new Date();
        return isSameDay(startAtDate, date);
    }, [startAtDate, canParticipate]);

    const {
        url: imageUrl = null,
        width: imageWidth = null,
        height: imageHeight = null,
    } = getImageAtSize(image, 'medium') || {};

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles[type]]: typeof styles[type] !== 'undefined',
                    [styles.locked]: canPlay === false,
                    [styles.isToday]: isToday,
                },
                className,
            ])}
        >
            <div className={styles.inner}>
                <Button
                    href={isStarted && !isEnded && explanationModal === null ? url : null}
                    onClick={!modalOpen && explanationModal !== null ? openModal : null}
                    disabled={!canPlay}
                    className={classNames([
                        styles.card,
                        {
                            [styles.disabled]: !canPlay,
                            [cardClassName]: cardClassName !== null,
                        },
                    ])}
                >
                    {imageUrl !== null ? (
                        <div
                            className={styles.imageContainer}
                            style={{
                                paddingBottom: `${(imageHeight / imageWidth) * 100}%`,
                            }}
                        >
                            {canPlay ? (
                                <div className={styles.dayPill}>
                                    <FormattedDate value={startAtDate} day="2-digit" />
                                </div>
                            ) : null}
                            <img src={imageUrl} alt={title} className={styles.image} />
                        </div>
                    ) : null}
                    <span className={styles.button}>
                        {hasParticipation ? (
                            <CircleCheckIcon className={styles.checkIcon} />
                        ) : (
                            <LockIcon locked={!canPlay} className={styles.lockIcon} />
                        )}

                        {canPlay && hasParticipation ? (
                            <FormattedMessage defaultMessage="Replay" description="Button label" />
                        ) : null}
                        {canPlay && !hasParticipation ? (
                            <FormattedMessage defaultMessage="Play" description="Button label" />
                        ) : null}
                        {!canPlay ? (
                            <FormattedDate value={startAtDate} day="numeric" month="long" />
                        ) : null}
                    </span>
                </Button>
            </div>
            {modalOpen ? (
                <ExplanationModal
                    blocks={explanationModal}
                    url={isStarted && !isEnded ? url : null}
                    onClose={closeModal}
                />
            ) : null}
        </div>
    );
}

IgaNoelItem.propTypes = propTypes;
IgaNoelItem.defaultProps = defaultProps;

export default IgaNoelItem;
